// Bloques B/C/D/E/F/G de 008 (Fase 3, Tanda 17, ver PLAN.md "FASE 3 — spec
// completa"). Bloque A ya existe vía `EncabezadoMspCampos`. Bloques H-P
// quedan para la Tanda 18, que también arma `Formulario008.tsx` y conecta
// todo (esta tanda entrega código sin registrar en `FORMULARIOS_DISCRETOS`).
//
// Decisiones de diseño (ambigüedad de producto sin impacto clínico, PLAN.md
// no las resuelve — se deciden y se marcan, mismo criterio de toda la Fase 3):
// - D (accidente/violencia/intoxicación) y E (antecedentes) son checklists
//   multi-marca (`ChecklistGrid`), no radio — el instructivo oficial dice
//   "para el resto de variables... se registra una X" sin indicar
//   exclusividad.
// - G (Glasgow): 3 subcampos independientes (ocular/verbal/motora); el total
//   (3-15) se calcula para mostrar en pantalla, no se persiste como campo
//   aparte — mismo criterio que los totales de E/F de 019 y H/I de 018A.
// - E "no aplica" es un checkbox informativo simple, sin lógica de ocultar
//   el resto del bloque — no hay precedente en el repo de ese patrón
//   condicional, no se inventa uno nuevo.
import { textoSiNo, type SiNoValor } from './camposSimplesDatos';
import { totalGlasgow } from './calculosClinicosDatos';
import type { Par } from './index';
import type { Traducir } from './tecnicaAnestesicaDatos';

function textosSiNo(t: Traducir): { si: string; no: string } {
  return { si: t('formulario.si'), no: t('formulario.no') };
}

function paresDeChecklist(t: Traducir, prefijoI18n: string, opciones: readonly string[], valores: Record<string, boolean>): Par {
  return { pregunta: t(`${prefijoI18n}.titulo`), respuesta: opciones.filter((c) => valores[c]).map((c) => t(`${prefijoI18n}.opciones.${c}`)).join(', ') };
}

// ---------- B — Registro de admisión ----------

export const ESTADO_CIVIL_OPCIONES = ['soltero', 'casado', 'divorciado', 'viudo', 'union', 'unionHecho', 'noAplica'] as const;
export type EstadoCivil = (typeof ESTADO_CIVIL_OPCIONES)[number];

export const CONDICION_EDAD_OPCIONES = ['horas', 'dias', 'meses', 'anios'] as const;
export type CondicionEdad = (typeof CONDICION_EDAD_OPCIONES)[number];

/** Tipo de documento de identificación impreso en B (PDF p. 35): CC/CI, PAS, CARNÉ, S/D. */
export const TIPO_DOCUMENTO_OPCIONES = ['ccci', 'pas', 'carne', 'sd'] as const;
export type TipoDocumento = (typeof TIPO_DOCUMENTO_OPCIONES)[number];

export const SEGURO_SALUD_OPCIONES = ['iessG', 'iessC', 'isspol', 'issfa', 'privado', 'ninguno'] as const;
export type SeguroSalud = (typeof SEGURO_SALUD_OPCIONES)[number];

export const FORMA_LLEGADA_OPCIONES = ['ambulatorio', 'ambulancia', 'otroTransporte'] as const;
export type FormaLlegada = (typeof FORMA_LLEGADA_OPCIONES)[number];

export interface EstadoBloqueB008 {
  /** aaaa-mm-dd (PDF p. 35, B). Lectura tolerante: los guardados anteriores no lo traen. */
  readonly fechaAdmision: string;
  readonly nombreAdmisionista: string;
  readonly tipoDocumento: TipoDocumento | '';
  readonly primerApellido: string;
  readonly segundoApellido: string;
  readonly primerNombre: string;
  readonly segundoNombre: string;
  readonly estadoCivil: EstadoCivil | '';
  readonly sexo: string;
  readonly telefonoFijo: string;
  readonly telefonoCelular: string;
  readonly fechaNacimiento: string;
  readonly lugarNacimiento: string;
  readonly nacionalidad: string;
  readonly edad: string;
  readonly condicionEdad: CondicionEdad | '';
  /** Par SI/NO impreso (PDF p. 35, B): tri-estado, ver `camposSimplesDatos.SiNoValor`. */
  readonly grupoPrioritario: SiNoValor;
  /** Par SI/NO impreso (PDF p. 35, B, esquina superior derecha). */
  readonly historiaClinicaEnEstablecimiento: SiNoValor;
  readonly grupoPrioritarioEspecifique: string;
  readonly autoidentificacionEtnica: string;
  readonly nacionalidadEtnica: string;
  readonly pueblos: string;
  readonly nivelEducacion: string;
  readonly estadoNivelEducacion: string;
  readonly tipoEmpresaTrabajo: string;
  readonly ocupacionProfesion: string;
  readonly seguroSalud: SeguroSalud | '';
  readonly provincia: string;
  readonly canton: string;
  readonly parroquia: string;
  readonly barrioSector: string;
  readonly callePrincipal: string;
  readonly calleSecundaria: string;
  readonly referenciaDomicilio: string;
  readonly contactoNombre: string;
  readonly contactoParentesco: string;
  readonly contactoDireccion: string;
  readonly contactoTelefono: string;
  readonly formaLlegada: FormaLlegada | '';
  readonly fuenteInformacion: string;
  readonly institucionOPersonaEntrega: string;
  readonly telefonoEntrega: string;
}

export function estadoInicialBloqueB008(): EstadoBloqueB008 {
  return {
    fechaAdmision: '',
    nombreAdmisionista: '',
    tipoDocumento: '',
    primerApellido: '',
    segundoApellido: '',
    primerNombre: '',
    segundoNombre: '',
    estadoCivil: '',
    sexo: '',
    telefonoFijo: '',
    telefonoCelular: '',
    fechaNacimiento: '',
    lugarNacimiento: '',
    nacionalidad: '',
    edad: '',
    condicionEdad: '',
    grupoPrioritario: '',
    historiaClinicaEnEstablecimiento: '',
    grupoPrioritarioEspecifique: '',
    autoidentificacionEtnica: '',
    nacionalidadEtnica: '',
    pueblos: '',
    nivelEducacion: '',
    estadoNivelEducacion: '',
    tipoEmpresaTrabajo: '',
    ocupacionProfesion: '',
    seguroSalud: '',
    provincia: '',
    canton: '',
    parroquia: '',
    barrioSector: '',
    callePrincipal: '',
    calleSecundaria: '',
    referenciaDomicilio: '',
    contactoNombre: '',
    contactoParentesco: '',
    contactoDireccion: '',
    contactoTelefono: '',
    formaLlegada: '',
    fuenteInformacion: '',
    institucionOPersonaEntrega: '',
    telefonoEntrega: '',
  };
}

function paresDeBloqueB008(t: Traducir, e: EstadoBloqueB008): Par[] {
  return [
    { pregunta: t('formularios.f008.bloqueB.fechaAdmision'), respuesta: (e.fechaAdmision ?? '').trim() },
    { pregunta: t('formularios.f008.bloqueB.admisionista'), respuesta: (e.nombreAdmisionista ?? '').trim() },
    { pregunta: t('formularios.f008.bloqueB.historiaClinicaEnEstablecimiento'), respuesta: textoSiNo(e.historiaClinicaEnEstablecimiento, textosSiNo(t)) },
    { pregunta: t('formularios.f008.bloqueB.primerApellido'), respuesta: e.primerApellido.trim() },
    { pregunta: t('formularios.f008.bloqueB.segundoApellido'), respuesta: e.segundoApellido.trim() },
    { pregunta: t('formularios.f008.bloqueB.primerNombre'), respuesta: e.primerNombre.trim() },
    { pregunta: t('formularios.f008.bloqueB.segundoNombre'), respuesta: e.segundoNombre.trim() },
    { pregunta: t('formularios.f008.bloqueB.tipoDocumento'), respuesta: e.tipoDocumento ? t(`formularios.f008.bloqueB.tipoDocumentoOpciones.${e.tipoDocumento}`) : '' },
    { pregunta: t('formularios.f008.bloqueB.estadoCivil'), respuesta: e.estadoCivil ? t(`formularios.f008.bloqueB.estadoCivilOpciones.${e.estadoCivil}`) : '' },
    { pregunta: t('formularios.f008.bloqueB.sexo'), respuesta: e.sexo.trim() },
    { pregunta: t('formularios.f008.bloqueB.telefonoFijo'), respuesta: e.telefonoFijo.trim() },
    { pregunta: t('formularios.f008.bloqueB.telefonoCelular'), respuesta: e.telefonoCelular.trim() },
    { pregunta: t('formularios.f008.bloqueB.fechaNacimiento'), respuesta: e.fechaNacimiento.trim() },
    { pregunta: t('formularios.f008.bloqueB.lugarNacimiento'), respuesta: e.lugarNacimiento.trim() },
    { pregunta: t('formularios.f008.bloqueB.nacionalidad'), respuesta: e.nacionalidad.trim() },
    { pregunta: t('formularios.f008.bloqueB.edad'), respuesta: e.edad.trim() },
    { pregunta: t('formularios.f008.bloqueB.condicionEdad'), respuesta: e.condicionEdad ? t(`formularios.f008.bloqueB.condicionEdadOpciones.${e.condicionEdad}`) : '' },
    { pregunta: t('formularios.f008.bloqueB.grupoPrioritario'), respuesta: textoSiNo(e.grupoPrioritario, textosSiNo(t)) },
    { pregunta: t('formularios.f008.bloqueB.grupoPrioritarioEspecifique'), respuesta: e.grupoPrioritarioEspecifique.trim() },
    { pregunta: t('formularios.f008.bloqueB.autoidentificacionEtnica'), respuesta: e.autoidentificacionEtnica.trim() },
    { pregunta: t('formularios.f008.bloqueB.nacionalidadEtnica'), respuesta: e.nacionalidadEtnica.trim() },
    { pregunta: t('formularios.f008.bloqueB.pueblos'), respuesta: e.pueblos.trim() },
    { pregunta: t('formularios.f008.bloqueB.nivelEducacion'), respuesta: e.nivelEducacion.trim() },
    { pregunta: t('formularios.f008.bloqueB.estadoNivelEducacion'), respuesta: e.estadoNivelEducacion.trim() },
    { pregunta: t('formularios.f008.bloqueB.tipoEmpresaTrabajo'), respuesta: e.tipoEmpresaTrabajo.trim() },
    { pregunta: t('formularios.f008.bloqueB.ocupacionProfesion'), respuesta: e.ocupacionProfesion.trim() },
    { pregunta: t('formularios.f008.bloqueB.seguroSalud'), respuesta: e.seguroSalud ? t(`formularios.f008.bloqueB.seguroSaludOpciones.${e.seguroSalud}`) : '' },
    { pregunta: t('formularios.f008.bloqueB.provincia'), respuesta: e.provincia.trim() },
    { pregunta: t('formularios.f008.bloqueB.canton'), respuesta: e.canton.trim() },
    { pregunta: t('formularios.f008.bloqueB.parroquia'), respuesta: e.parroquia.trim() },
    { pregunta: t('formularios.f008.bloqueB.barrioSector'), respuesta: e.barrioSector.trim() },
    { pregunta: t('formularios.f008.bloqueB.callePrincipal'), respuesta: e.callePrincipal.trim() },
    { pregunta: t('formularios.f008.bloqueB.calleSecundaria'), respuesta: e.calleSecundaria.trim() },
    { pregunta: t('formularios.f008.bloqueB.referenciaDomicilio'), respuesta: e.referenciaDomicilio.trim() },
    { pregunta: t('formularios.f008.bloqueB.contactoNombre'), respuesta: e.contactoNombre.trim() },
    { pregunta: t('formularios.f008.bloqueB.contactoParentesco'), respuesta: e.contactoParentesco.trim() },
    { pregunta: t('formularios.f008.bloqueB.contactoDireccion'), respuesta: e.contactoDireccion.trim() },
    { pregunta: t('formularios.f008.bloqueB.contactoTelefono'), respuesta: e.contactoTelefono.trim() },
    { pregunta: t('formularios.f008.bloqueB.formaLlegada'), respuesta: e.formaLlegada ? t(`formularios.f008.bloqueB.formaLlegadaOpciones.${e.formaLlegada}`) : '' },
    { pregunta: t('formularios.f008.bloqueB.fuenteInformacion'), respuesta: e.fuenteInformacion.trim() },
    { pregunta: t('formularios.f008.bloqueB.institucionOPersonaEntrega'), respuesta: e.institucionOPersonaEntrega.trim() },
    { pregunta: t('formularios.f008.bloqueB.telefonoEntrega'), respuesta: e.telefonoEntrega.trim() },
  ];
}

// ---------- C — Inicio de atención ----------

export const CONDICION_LLEGADA_OPCIONES = ['estable', 'inestable', 'fallecido'] as const;
export type CondicionLlegada = (typeof CONDICION_LLEGADA_OPCIONES)[number];

export interface EstadoBloqueC008 {
  readonly fecha: string;
  readonly hora: string;
  readonly condicionLlegada: CondicionLlegada | '';
  readonly motivo: string;
}

export function estadoInicialBloqueC008(): EstadoBloqueC008 {
  return { fecha: '', hora: '', condicionLlegada: '', motivo: '' };
}

function paresDeBloqueC008(t: Traducir, e: EstadoBloqueC008): Par[] {
  return [
    { pregunta: t('formularios.f008.bloqueC.fecha'), respuesta: e.fecha.trim() },
    { pregunta: t('formularios.f008.bloqueC.hora'), respuesta: e.hora.trim() },
    { pregunta: t('formularios.f008.bloqueC.condicionLlegada'), respuesta: e.condicionLlegada ? t(`formularios.f008.bloqueC.condicionLlegadaOpciones.${e.condicionLlegada}`) : '' },
    { pregunta: t('formularios.f008.bloqueC.motivo'), respuesta: e.motivo.trim() },
  ];
}

// ---------- D — Accidente, violencia, intoxicación ----------

export const ACCIDENTE_VIOLENCIA_OPCIONES = [
  'accidenteTransito',
  'caida',
  'quemadura',
  'mordedura',
  'ahogamiento',
  'cuerpoExtrano',
  'aplastamiento',
  'otroAccidente',
  'violenciaArmaFuego',
  'violenciaArmaCortopunzante',
  'violenciaRina',
  'violenciaFamiliar',
  'presuntaViolenciaFisica',
  'presuntaViolenciaPsicologica',
  'presuntaViolenciaSexual',
  'intoxicacionAlcoholica',
  'intoxicacionAlimentaria',
  'intoxicacionPorDrogas',
  'inhalacionGases',
  'otraIntoxicacion',
  'picadura',
  'envenenamiento',
  'anafilaxia',
] as const;

export interface EstadoBloqueD008 {
  readonly fecha: string;
  readonly hora: string;
  readonly lugarEvento: string;
  readonly direccionEvento: string;
  /** Par SI/NO impreso (PDF p. 35, D). */
  readonly custodiaPolicial: SiNoValor;
  readonly tipos: Readonly<Record<string, boolean>>;
  /** Par SI/NO impreso (PDF p. 35, D). */
  readonly notificacion: SiNoValor;
  readonly sugestivoAlientoAlcoholico: boolean;
  readonly observaciones: string;
}

export function estadoInicialBloqueD008(): EstadoBloqueD008 {
  return { fecha: '', hora: '', lugarEvento: '', direccionEvento: '', custodiaPolicial: '', tipos: {}, notificacion: '', sugestivoAlientoAlcoholico: false, observaciones: '' };
}

function paresDeBloqueD008(t: Traducir, e: EstadoBloqueD008): Par[] {
  return [
    { pregunta: t('formularios.f008.bloqueD.fecha'), respuesta: e.fecha.trim() },
    { pregunta: t('formularios.f008.bloqueD.hora'), respuesta: e.hora.trim() },
    { pregunta: t('formularios.f008.bloqueD.lugarEvento'), respuesta: e.lugarEvento.trim() },
    { pregunta: t('formularios.f008.bloqueD.direccionEvento'), respuesta: e.direccionEvento.trim() },
    { pregunta: t('formularios.f008.bloqueD.custodiaPolicial'), respuesta: textoSiNo(e.custodiaPolicial, textosSiNo(t)) },
    paresDeChecklist(t, 'formularios.f008.bloqueD.tipos', ACCIDENTE_VIOLENCIA_OPCIONES, e.tipos),
    { pregunta: t('formularios.f008.bloqueD.notificacion'), respuesta: textoSiNo(e.notificacion, textosSiNo(t)) },
    { pregunta: t('formularios.f008.bloqueD.sugestivoAlientoAlcoholico'), respuesta: e.sugestivoAlientoAlcoholico ? t('formulario.si') : '' },
    { pregunta: t('formularios.f008.bloqueD.observaciones'), respuesta: e.observaciones.trim() },
  ];
}

// ---------- E — Antecedentes patológicos personales y familiares ----------

export const ANTECEDENTES_CATEGORIAS_OPCIONES = ['alergicos', 'clinicos', 'ginecologicos', 'traumatologicos', 'pediatricos', 'quirurgicos', 'farmacologicos', 'habitos', 'familiares', 'otros'] as const;

export interface EstadoBloqueE008 {
  readonly categorias: Readonly<Record<string, boolean>>;
  readonly describir: string;
  readonly noAplica: boolean;
}

export function estadoInicialBloqueE008(): EstadoBloqueE008 {
  return { categorias: {}, describir: '', noAplica: false };
}

function paresDeBloqueE008(t: Traducir, e: EstadoBloqueE008): Par[] {
  return [
    paresDeChecklist(t, 'formularios.f008.bloqueE.categorias', ANTECEDENTES_CATEGORIAS_OPCIONES, e.categorias),
    { pregunta: t('formularios.f008.bloqueE.describir'), respuesta: e.describir.trim() },
    { pregunta: t('formularios.f008.bloqueE.noAplica'), respuesta: e.noAplica ? t('formulario.si') : '' },
  ];
}

// ---------- G — Constantes vitales y antropometría (incluye Glasgow) ----------

export interface EstadoBloqueG008 {
  readonly sinConstantesVitales: boolean;
  readonly presionArterial: string;
  readonly pulso: string;
  readonly frecuenciaRespiratoria: string;
  readonly pulsioximetria: string;
  readonly perimetroCefalico: string;
  readonly peso: string;
  readonly talla: string;
  readonly glicemiaCapilar: string;
  readonly glasgowOcular: string;
  readonly glasgowVerbal: string;
  readonly glasgowMotora: string;
  readonly reaccionPupilarDerecha: string;
  readonly reaccionPupilarIzquierda: string;
  readonly tiempoLlenadoCapilar: string;
}

export function estadoInicialBloqueG008(): EstadoBloqueG008 {
  return {
    sinConstantesVitales: false,
    presionArterial: '',
    pulso: '',
    frecuenciaRespiratoria: '',
    pulsioximetria: '',
    perimetroCefalico: '',
    peso: '',
    talla: '',
    glicemiaCapilar: '',
    glasgowOcular: '',
    glasgowVerbal: '',
    glasgowMotora: '',
    reaccionPupilarDerecha: '',
    reaccionPupilarIzquierda: '',
    tiempoLlenadoCapilar: '',
  };
}

/** Suma los 3 subcampos de Glasgow (3-15) cuando los 3 están cargados y dentro de su rango (ocular 1-4, verbal 1-5, motor 1-6); si falta alguno o alguno es inválido, `null` (no se puede calcular un total parcial). */
export function totalGlasgow008(e: EstadoBloqueG008): number | null {
  return totalGlasgow(e.glasgowOcular, e.glasgowVerbal, e.glasgowMotora);
}

function paresDeBloqueG008(t: Traducir, e: EstadoBloqueG008): Par[] {
  const total = totalGlasgow008(e);
  return [
    { pregunta: t('formularios.f008.bloqueG.sinConstantesVitales'), respuesta: e.sinConstantesVitales ? t('formulario.si') : '' },
    { pregunta: t('formularios.f008.bloqueG.presionArterial'), respuesta: e.presionArterial.trim() },
    { pregunta: t('formularios.f008.bloqueG.pulso'), respuesta: e.pulso.trim() },
    { pregunta: t('formularios.f008.bloqueG.frecuenciaRespiratoria'), respuesta: e.frecuenciaRespiratoria.trim() },
    { pregunta: t('formularios.f008.bloqueG.pulsioximetria'), respuesta: e.pulsioximetria.trim() },
    { pregunta: t('formularios.f008.bloqueG.perimetroCefalico'), respuesta: e.perimetroCefalico.trim() },
    { pregunta: t('formularios.f008.bloqueG.peso'), respuesta: e.peso.trim() },
    { pregunta: t('formularios.f008.bloqueG.talla'), respuesta: e.talla.trim() },
    { pregunta: t('formularios.f008.bloqueG.glicemiaCapilar'), respuesta: e.glicemiaCapilar.trim() },
    { pregunta: t('formularios.f008.bloqueG.glasgowOcular'), respuesta: e.glasgowOcular.trim() },
    { pregunta: t('formularios.f008.bloqueG.glasgowVerbal'), respuesta: e.glasgowVerbal.trim() },
    { pregunta: t('formularios.f008.bloqueG.glasgowMotora'), respuesta: e.glasgowMotora.trim() },
    { pregunta: t('formularios.f008.bloqueG.glasgowTotal'), respuesta: total === null ? '' : String(total) },
    { pregunta: t('formularios.f008.bloqueG.reaccionPupilarDerecha'), respuesta: e.reaccionPupilarDerecha.trim() },
    { pregunta: t('formularios.f008.bloqueG.reaccionPupilarIzquierda'), respuesta: e.reaccionPupilarIzquierda.trim() },
    { pregunta: t('formularios.f008.bloqueG.tiempoLlenadoCapilar'), respuesta: e.tiempoLlenadoCapilar.trim() },
  ];
}

// ---------- Estado combinado B/C/D/E/F/G ----------

export interface EstadoBloques008Ingreso {
  readonly b: EstadoBloqueB008;
  readonly c: EstadoBloqueC008;
  readonly d: EstadoBloqueD008;
  readonly e: EstadoBloqueE008;
  readonly enfermedadActual: string;
  readonly g: EstadoBloqueG008;
}

export function estadoInicialBloques008Ingreso(): EstadoBloques008Ingreso {
  return {
    b: estadoInicialBloqueB008(),
    c: estadoInicialBloqueC008(),
    d: estadoInicialBloqueD008(),
    e: estadoInicialBloqueE008(),
    enfermedadActual: '',
    g: estadoInicialBloqueG008(),
  };
}

/** Serializa los bloques B/C/D/E/F/G a `Par[]`, listos para `onGuardado`. */
export function paresDeBloques008Ingreso(t: Traducir, estado: EstadoBloques008Ingreso): Par[] {
  const pares: Par[] = [
    ...paresDeBloqueB008(t, estado.b),
    ...paresDeBloqueC008(t, estado.c),
    ...paresDeBloqueD008(t, estado.d),
    ...paresDeBloqueE008(t, estado.e),
    { pregunta: t('formularios.f008.bloqueF.titulo'), respuesta: estado.enfermedadActual.trim() },
    ...paresDeBloqueG008(t, estado.g),
  ];
  return pares.filter((p) => p.respuesta.trim() !== '');
}
